// Nahrávání souborů na API (multipart/form-data) s průběhem: fetch průběh odesílání neumí, proto
// XMLHttpRequest se stejnými hlavičkami jako api/klient.ts (X-Requested-With, cookie relace stejného
// původu). Chyby mají stejný tvar ChybaApi { kod, zprava, detaily?, duvod? }; odpověď bez JSON (třeba
// limit velikosti na proxy) dostane srozumitelnou českou zprávu podle HTTP statusu.
import { ChybaApi, HLAVICKA_POZADAVKU, urlEndpointu, type Chyba } from './klient';

export interface MoznostiNahrani {
  /** Průběh odesílání 0–1 (volá se opakovaně; u malých souborů třeba jen jednou). */
  priPrubehu?: (podil: number) => void;
  signal?: AbortSignal;
}

function jeChyba(hodnota: unknown): hodnota is Chyba {
  return (
    typeof hodnota === 'object' &&
    hodnota !== null &&
    typeof (hodnota as Chyba).kod === 'string' &&
    typeof (hodnota as Chyba).zprava === 'string'
  );
}

/** Chyba pro odpověď bez těla `Chyba` (proxy, výpadek) — podle statusu. */
export function chybaPodleStatusu(status: number): ChybaApi {
  if (status === 413) {
    return new ChybaApi(
      {
        kod: 'soubor.prilis_velky',
        zprava: 'Soubor je příliš velký — server ho nepřijal. Fotku zmenšete, dokument rozdělte.',
      },
      status,
    );
  }
  if (status === 415) {
    return new ChybaApi(
      {
        kod: 'soubor.nepodporovany_typ',
        zprava: 'Tento typ souboru server nepřijímá.',
      },
      status,
    );
  }
  if (status === 401) {
    return new ChybaApi(
      { kod: 'neprihlasen', zprava: 'Přihlášení vypršelo — přihlaste se znovu a soubor nahrajte.' },
      status,
    );
  }
  return new ChybaApi(
    {
      kod: 'server.neplatna_odpoved',
      zprava:
        status >= 500
          ? 'Server hlásí chybu. Zkuste to za chvíli znovu; pokud potíže trvají, dejte vědět správci.'
          : `Server odpověděl neočekávaně (HTTP ${status}).`,
    },
    status,
  );
}

/** POST multipart na `cesta` (relativní k /api/v1) s průběhem; vrací JSON odpovědi. */
export function nahratFormular<T>(
  cesta: string,
  data: FormData,
  moznosti: MoznostiNahrani = {},
): Promise<T> {
  const { priPrubehu, signal } = moznosti;
  return new Promise<T>((vyridit, odmitnout) => {
    if (signal?.aborted) {
      odmitnout(new DOMException('Nahrávání zrušeno.', 'AbortError'));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', urlEndpointu(cesta));
    xhr.setRequestHeader('Accept', 'application/json');
    for (const [nazev, hodnota] of Object.entries(HLAVICKA_POZADAVKU)) {
      xhr.setRequestHeader(nazev, hodnota);
    }
    xhr.upload.onprogress = (udalost) => {
      if (udalost.lengthComputable && udalost.total > 0) {
        priPrubehu?.(Math.min(1, udalost.loaded / udalost.total));
      }
    };
    xhr.onload = () => {
      let telo: unknown;
      try {
        telo = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : undefined;
      } catch {
        telo = undefined;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        priPrubehu?.(1);
        vyridit(telo as T);
        return;
      }
      odmitnout(jeChyba(telo) ? new ChybaApi(telo, xhr.status) : chybaPodleStatusu(xhr.status));
    };
    xhr.onerror = () =>
      odmitnout(
        new ChybaApi(
          {
            kod: 'sit.nedostupna',
            zprava:
              'Soubor se nepodařilo odeslat — server neodpovídá. Zkontrolujte připojení a zkuste to znovu.',
          },
          0,
        ),
      );
    xhr.onabort = () => odmitnout(new DOMException('Nahrávání zrušeno.', 'AbortError'));
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(data);
  });
}
